import { Button, Form, Input, notification } from 'antd';
import { CloseCircleOutlined, FileImageOutlined, SendOutlined, QrcodeOutlined } from '@ant-design/icons';
import { useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import styled from 'styled-components';

import { AppContext } from 'context/AppContext';
import { Campaign, Task, Enrollment } from 'models/CommonType';
import { Notification } from 'services/types/ProfileTypes';
import { SubmissionResponse } from 'services/types/TaskTypes';
import SubmissionModal from './SubmissionModal';
import SubmissionDrawer from './SubmissionDrawer';
import { QRScannerModal } from 'components/QRScanner';


const Banner = styled.div`
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background: linear-gradient(135deg, #F8F9FA 0%, #F1F3F4 100%);
  border-top: 1px solid #E8EAED;
  backdrop-filter: blur(10px);
  z-index: 1001 !important;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  transform: translateZ(0);
  will-change: transform;
`;

const BannerContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 24px;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 12px;
  }
`;

const SubmissionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const InputContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 24px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #E8EAED;
  transition: all 0.2s ease;
  
  &:focus-within {
    border-color: #9BC53D;
    box-shadow: 0 0 0 3px rgba(155, 197, 61, 0.1);
  }
  
  .ant-input {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    padding: 12px 16px !important;
    font-size: 14px !important;
    
    &:focus {
      border: none !important;
      box-shadow: none !important;
    }
    
    &::placeholder {
      color: #9AA0A6;
    }
  }
  
  @media (max-width: 768px) {
    .ant-input {
      padding: 10px 12px !important;
      font-size: 14px !important;
    }
  }
`;

const ActionButton = styled(Button)`
  height: 44px !important;
  min-width: 44px !important;
  border-radius: 22px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
  
  &:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16) !important;
  }
  
  &:active {
    transform: translateY(0) !important;
  }
  
  @media (max-width: 768px) {
    height: 40px !important;
    min-width: 40px !important;
    border-radius: 20px !important;
  }
`;

const PhotoButton = styled(ActionButton)`
  background: #6C757D !important;
  color: white !important;
  
  &:hover {
    background: #5A6268 !important;
    color: white !important;
  }
  
  &:disabled {
    background: #ADB5BD !important;
    color: white !important;
  }
`;

const QRButton = styled(ActionButton)`
  background: #17A2B8 !important;
  color: white !important;
  
  &:hover {
    background: #138496 !important;
    color: white !important;
  }
  
  &:disabled {
    background: #ADB5BD !important;
    color: white !important;
  }
`;

const SubmitButton = styled(ActionButton)`
  background: linear-gradient(135deg, #9BC53D, #7BA428) !important;
  color: white !important;
  
  &:hover {
    background: linear-gradient(135deg, #8AB12A, #6A9320) !important;
    color: white !important;
  }
  
  &:disabled {
    background: #DDE5D0 !important;
    color: #9AA0A6 !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06) !important;
  }
`;

const StatusMessage = styled.div`
  text-align: center;
  padding: 16px;
  color: #5F6368;
  font-size: 15px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    padding: 12px;
    font-size: 14px;
  }
`

interface SubmissionBannerProps {
  task: Task;
  campaign: Campaign;
  enrollment?: Enrollment | null;
  submitHandler?: () => void;
}

const SubmissionBanner: React.FC<SubmissionBannerProps> = ({
  task,
  campaign,
  enrollment,
  submitHandler,
}) => {
  const { t } = useTranslation();
  const { api } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [answer, setAnswer] = useState('');
  const [submissionId, setSubmissionId] = useState<string>();
  const [approvedNotification, setApprovedNotification] = useState<Notification>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [form] = Form.useForm();
  const addPhoto = task.task_type === 'A_COMMENT' ||
    task.task_type === 'A_LIKE' ||
    task.task_type === 'M_PHOTO';
  const showQRScanner = task.task_type === 'A_TEXT';

  const drawerOpen = useCallback(() => setIsDrawerOpen(true), []);
  const drawerCloseHandler = useCallback(() => setIsDrawerOpen(false), []);
  const modalCloseHandler = useCallback(() => setSubmissionId(undefined), []);
  const qrScannerOpen = useCallback(() => setIsQRScannerOpen(true), []);
  const qrScannerCloseHandler = useCallback(() => setIsQRScannerOpen(false), []);

  const handleQRScanSuccess = useCallback(async (scannedText: string) => {
    setAnswer(scannedText);
    form.setFieldsValue({ submission: scannedText });
    setIsQRScannerOpen(false);
    
    // Auto-submit the scanned text
    setIsLoading(true);
    
    const submissionResponse: SubmissionResponse = await api.task.submitTaskAnswers({
      taskId: task.id,
      message: scannedText,
      images: undefined,
    });

    if (submissionResponse.is_approved) {
      const notification = (await api.profile.getNotifiactions())
        .results
        ?.find((notification) => notification.data?.object_id === submissionResponse.id);
      setApprovedNotification(notification);
    }

    setIsLoading(false);

    if (!isEmpty(submissionResponse.error)) {
      if (submissionResponse.error.data.code === 'already_submitted') {
        notification.open({
          message: t('#Task.error.already_submitted_title'),
          description: t('#Task.error.already_submitted'),
          icon: <CloseCircleOutlined style={{ color: '#eb2f96' }} />,
        });
      } else {
        notification.open({
          message: t('#Task.error.error_title'),
          description: submissionResponse.error.data.message,
          icon: <CloseCircleOutlined style={{ color: '#eb2f96' }} />,
        });
      }
    } else {
      setSubmissionId(submissionResponse.id);
    }
    submitHandler?.();
  }, [api, task.id, t, submitHandler, form]);

  // Helper function to generate team message based on iOS logic
  const getTeamMessage = () => {
    if ((campaign.min_team_member || 0) > 0) {
      return t('#Task.team_range_message', { 
        min: campaign.min_team_member, 
        max: campaign.max_team_member 
      });
    } else {
      return t('#Task.team_upper_range_message', { 
        max: campaign.max_team_member 
      });
    }
  };

  const onSubmit = async (values: any) => {
    setIsLoading(true);

    const submissionResponse: SubmissionResponse = await api.task.submitTaskAnswers({
      taskId: task.id,
      message: values.submission,
      images: values.images,
    });

    if (submissionResponse.is_approved) {
      const notification = (await api.profile.getNotifiactions())
        .results
        ?.find((notification) => notification.data?.object_id === submissionResponse.id);
      setApprovedNotification(notification);
    }

    setIsLoading(false);

    if (!isEmpty(submissionResponse.error)) {
      if (submissionResponse.error.data.code === 'already_submitted') {
        notification.open({
          message: t('#Task.error.already_submitted_title'),
          description: t('#Task.error.already_submitted'),
          icon: <CloseCircleOutlined style={{ color: '#eb2f96' }} />,
        });
      } else {
        notification.open({
          message: t('#Task.error.error_title'),
          description: submissionResponse.error.data.message,
          icon: <CloseCircleOutlined style={{ color: '#eb2f96' }} />,
        });
      }
    } else {
      setSubmissionId(submissionResponse.id);
    }
    submitHandler?.();
  };

  const component = () => {
    const isExpired = task.expired ? new Date(task.expired).getTime() < Date.now() : false;
    
    // Following iOS logic order:
    // 1. Check if user is anonymous (we don't have this check in web)
    // 2. Check if task hasn't started yet
    // 3. Check if task is expired
    // 4. Check if task is submitted
    // 5. Check if task requires team and team is not ready
    // 6. Check if prerequisite task exists and not submitted
    // 7. Show submission form if enrolled
    
    if (task.is_approved) {
      return (
        <StatusMessage>
          {t('#Task.approved')}
        </StatusMessage>
      );
    } else if (isExpired) {
      return (
        <StatusMessage>
          {t('#Task.expired')}
        </StatusMessage>
      );
    } else if (task.is_submitted) {
      return (
        <StatusMessage>
          {t('#Task.submitted_pending')}
        </StatusMessage>
      );
    } else if (task.is_require_team && !(enrollment?.is_team_ready ?? true)) {
      return (
        <StatusMessage>
          {getTeamMessage()}
        </StatusMessage>
      );
    } else if (task.prerequisite_task && !task.prerequisite_task.is_approved) {
      return (
        <div style={{ padding: '16px 0' }}>
          <Link to={`/task?id=${task.prerequisite_task.id}`}>
            <SubmitButton style={{ width: '100%', borderRadius: '24px !important', height: '48px !important' }}>
              {t('#Task.prerequisite')}
            </SubmitButton>
          </Link>
        </div>
      );
    } else if (enrollment?.campaign.is_joined) {
      return (
        <Form
          form={form}
          onFinish={onSubmit}
          onValuesChange={(_changedValues, allValues) => setAnswer(allValues.submission)}
          initialValues={{ submission: answer }}
        >
          <SubmissionContainer>
            <InputContainer>
              <Form.Item name="submission" style={{ margin: 0, flex: 1 }}>
                <Input
                  id="task_submission"
                  placeholder={t('#Task.submission_placeholder')}
                  readOnly={addPhoto}
                  onClick={addPhoto ? drawerOpen : undefined}
                />
              </Form.Item>
            </InputContainer>

            {showQRScanner && (
              <Form.Item style={{ margin: 0 }}>
                <QRButton
                  icon={<QrcodeOutlined />}
                  onClick={qrScannerOpen}
                  title={t('#Task.scan_qr')}
                />
              </Form.Item>
            )}

            {addPhoto && (
              <Form.Item style={{ margin: 0 }}>
                <PhotoButton
                  icon={<FileImageOutlined />}
                  onClick={drawerOpen}
                />
              </Form.Item>
            )}

            <Form.Item style={{ margin: 0 }}>
              <SubmitButton
                htmlType="submit"
                icon={<SendOutlined />}
                disabled={answer === ''}
                loading={isLoading}
              />
            </Form.Item>
          </SubmissionContainer>
        </Form>
      );
    } else if (task.is_submitted && !task.is_approved) {
      return (
        <StatusMessage>
          {t('#Task.submitted_pending')}
        </StatusMessage>
      );
    }
    return undefined;
  };
  const bannerComponent = component();

  return (
    <>
      {bannerComponent && <>
        <div style={{ paddingBottom: 80 }} />
        <Banner>
          <BannerContent>
            {bannerComponent}
          </BannerContent>
        </Banner>
      </>}

      <SubmissionDrawer
        isOpen={isDrawerOpen}
        closeHandler={drawerCloseHandler}
        taskId={task.id}
        isLoading={isLoading}
        submitHandler={onSubmit} />

      <SubmissionModal
        isOpen={!!submissionId}
        submissionId={submissionId}
        approvedNotification={approvedNotification}
        closeHandler={modalCloseHandler} />

      <QRScannerModal
        isOpen={isQRScannerOpen}
        onClose={qrScannerCloseHandler}
        onScanSuccess={handleQRScanSuccess}
      />
    </>
  );
}

export default SubmissionBanner;
